import { memo, useCallback, useMemo, useState } from "react";

import { ArrowRight } from "components/icons/generated";
import Icon from "ds/components/Icon";

import { diffStructuredValues } from "../helpers";
import RunCodeChangesExpandableHeader from "../ExpandableHeader";
import RunCodeChangesPrefix from "../Prefix";
import type { RunCodeChangesStructuredFormat } from "../types";
import RunCodeChangesStructuredDiffContent from "./DiffContent";
import styles from "./styles.module.css";

type RunCodeChangesStructuredRowProps = {
  name: string;
  prefix: string;
  color: string | undefined;
  format: RunCodeChangesStructuredFormat;
  before: string | undefined;
  after: string | undefined;
  afterUnknown?: boolean;
};

const RunCodeChangesStructuredRow = memo(
  ({
    name,
    prefix,
    color,
    format,
    before,
    after,
    afterUnknown,
  }: RunCodeChangesStructuredRowProps) => {
    const [isExpanded, setIsExpanded] = useState(false);

    const handleToggle = useCallback(() => {
      setIsExpanded((prev) => !prev);
    }, []);

    const diffResult = useMemo(
      () => (isExpanded ? diffStructuredValues(before, after, format) : undefined),
      [isExpanded, before, after, format]
    );

    return (
      <>
        <RunCodeChangesExpandableHeader isExpanded={isExpanded} onToggle={handleToggle}>
          <RunCodeChangesPrefix prefix={prefix} color={color} />
          <span className={styles.name}>{name}:</span>
          <span className={styles.label}>{format}</span>
          {afterUnknown && (
            <>
              <Icon src={ArrowRight} className={styles.arrow} />
              <span className={styles.knownAfterApply}>(known after apply)</span>
            </>
          )}
        </RunCodeChangesExpandableHeader>
        {isExpanded && diffResult && <RunCodeChangesStructuredDiffContent lines={diffResult} />}
      </>
    );
  }
);

RunCodeChangesStructuredRow.displayName = "RunCodeChangesStructuredRow";

export default RunCodeChangesStructuredRow;
